// Heap.tsx
import { useEffect } from "react";

type StoreKey = string;
type StoreValue = any;
const DEFAULT_SCRIPT_ID = "heap-3e";
const heapStore = new Map<StoreKey, StoreValue>();

const getHeapScript = (id: string | number): string => `
  window.heap=window.heap||[],heap.load=function(e,t){
    window.heap.appid=e,window.heap.config=t=t||{};
    var r=document.createElement("script");
    r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";
    var a=document.getElementsByTagName("script")[0];
    a.parentNode.insertBefore(r,a);
    for(var n=function(e){
      return function(){
        heap.push([e].concat(Array.prototype.slice.call(arguments,0)))
      }
    },p=["addEventProperties","addUserProperties","clearEventProperties","identify",
    "resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;
    o<p.length;o++)heap[p[o]]=n(p[o])
  };
  heap.load("${id}");
`;


export const initialize = (id: string | number): void => {
    try {
        if (!id) {
            throw new Error("You need to provide your Heap Analytics ID.");
        }

        if (!document) {
            console.warn("Document not available, skipping Heap initialization");
            return;
        }

        // Safe setup logic - ensure window.heap exists
        if (!window.heap) {
            window.heap = window.heap || [];
        }

        const hasScript = !!document.getElementById(DEFAULT_SCRIPT_ID);
        const heapObj = window.heap || undefined;

        if (hasScript || (heapObj && heapObj?.track && heapObj.appid)) {
            console.log("Heap already initialized, skipping");
            return;
        }

        const script = document.createElement("script");

        script.innerHTML = getHeapScript(id);
        script.id = DEFAULT_SCRIPT_ID;
        script.async = true;

        // Add error handling for script loading
        script.onerror = (error) => {
            console.error("Failed to load Heap script:", error);
        };

        script.onload = () => {
            console.log("Heap script loaded successfully");
        };

        document.body.appendChild(script);
        console.log("Heap initialization started with ID:", id);

    } catch (error) {
        console.error("Heap initialization failed:", error);
        // Don't throw the error to prevent breaking the app
    }
};
function isEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (a === null || b === null) return false;

  if (typeof a !== "object") return false;

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqual(a[key], b[key])) return false;
  }

  return true;
}
// Function to set a value in the cache with a deep equality check
export const setHeapStore = (key: string, value: any): void => {
    try {
        const existingValue = heapStore.get(key);
        const heapObj = window.heap || undefined;
        
        if (heapObj && heapObj?.loaded == true) {
            if (!isEqual(existingValue, value)) {
                heapStore.set(key, value);
                heapObj.track(key, value);
            }
        } else {
            console.warn("Heap not loaded or not available, skipping tracking for:", key);
        }
    } catch (error) {
        console.error("Failed to set Heap store:", error);
    }
};

export const trackEvents = (
    key : string,
    value: any
): void => {
    try {
        if (!key || !value) {
            console.warn("Key and value must be provided for tracking, skipping:", { key, value });
            return;
        }
        console.log("Tracking event:", key);
        setHeapStore(key, value);
    } catch (error) {
        console.error("Failed to track event:", error);
    }
};

// Configure user properties for Heap analytics
export const configureUserProperties = (
    user: any,
    userRole: any,
    userProfile: any,
    getDisplayName: () => string
): void => {
    try {
        // Safe setup logic - ensure window.heap exists
        if (!window.heap) {
            window.heap = window.heap || [];
            console.warn('Heap not initialized, creating placeholder');
        }

        if (!user) {
            console.warn('User not provided, skipping Heap user configuration');
            return;
        }

        // Use Okta user ID for identification
        const userId = user.sub || user.email; // sub is the Okta user ID
        
        if (!userId) {
            console.warn('No user ID available for Heap identification');
            return;
        }

        // Safely call identify
        if (window.heap.identify) {
            window.heap.identify(userId);
        } else {
            console.warn('Heap identify method not available, queuing for later');
            window.heap.push(['identify', userId]);
        }
        
        // Set user properties
        const userProperties = {
            email: user.email,
            name: user.name || (getDisplayName ? getDisplayName() : 'Unknown'),
            role: userRole?.isAdmin ? 'Admin' : 'Basic User',
            organization: 'N/A', // Organization info not available in current UserProfile
            // Additional properties from Okta claims
            preferred_username: user.preferred_username,
            given_name: user.given_name,
            family_name: user.family_name,
            // Additional role information
            userId: userRole?.userId,
            roleDetails: userRole?.role,
        };

        // Filter out undefined values
        const filteredProperties = Object.fromEntries(
            Object.entries(userProperties).filter(([_, value]) => value !== undefined && value !== null && value !== '')
        );

        // Safely call addUserProperties
        if (window.heap.addUserProperties) {
            window.heap.addUserProperties(filteredProperties);
        } else {
            console.warn('Heap addUserProperties method not available, queuing for later');
            window.heap.push(['addUserProperties', filteredProperties]);
        }
        
        console.log('Heap user properties configured:', filteredProperties);

    } catch (error) {
        console.error('Failed to configure Heap user properties:', error);
        // Don't throw the error to prevent breaking the app
    }
};

// Modular event tracking helper
export const trackHeapEvent = (event: string, properties?: Record<string, any>): void => {
    try {
        if (!event) {
            console.warn("Event name is required for Heap tracking");
            return;
        }

        // Safe setup logic - ensure window.heap exists
        if (!window.heap) {
            window.heap = window.heap || [];
            console.warn('Heap not initialized, creating placeholder for event:', event);
        }

        // Add timestamp to all events
        const eventProperties = {
            timestamp: new Date().toISOString(),
            ...properties
        };

        // Safely call track method
        if (window.heap.track) {
            window.heap.track(event, eventProperties);
        } else {
            console.warn('Heap track method not available, queuing event for later:', event);
            window.heap.push(['track', event, eventProperties]);
        }

        console.log(`Heap event tracked: ${event}`, eventProperties);

    } catch (error) {
        console.warn("Heap tracking failed:", error);
        // Don't throw the error to prevent breaking the app
    }
};

const Heap3E = {
    initialize,
    trackEvents,
    configureUserProperties,
    trackHeapEvent
};

export default Heap3E;